// ScoreSection Component
import React from 'react'
import { useRef } from 'react'
import { IoMdDownload } from 'react-icons/io'
import Moment from 'react-moment'
import spinner from '../assets/svg/spinner.svg'
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from 'react-circular-progressbar'
import { FaCircleInfo } from 'react-icons/fa6'
import { FaShare } from 'react-icons/fa'
import useAbility from '@/hooks/use-ability'
import UpgradeLockIconLayout from './upgrade-lock-icon-layout'
import UpgradeLayout from './upgrade-layout'

const ScoreSection = ({
  title,
  value,
  hoverText,
  color,
  onInfoClick,
  needUpgrade,
}) => (
  <div className="text-center w-[6rem]">
    <div className="text-sm font-semibold">{title}</div>
    <div className="w-[4rem] mx-auto relative" title={hoverText}>
      <UpgradeLockIconLayout
        className="h-full rounded-full items-center justify-center opacity-80"
        iconSize={{ width: 24, height: 24 }}
        show={needUpgrade}
      />
      <CircularProgressbarWithChildren
        value={value === 'NR' ? 0 : parseInt(value)}
        background
        backgroundPadding={6}
        styles={buildStyles({
          backgroundColor: color,
          pathColor: '#fff',
          trailColor: 'transparent',
        })}
      >
        <strong className="text-white text-xs" style={{ fontSize: 16 }}>
          {value}
        </strong>
        {!needUpgrade && (
          <button
            onClick={onInfoClick}
            className="absolute top-0 right-0 h-5 w-5 flex items-center justify-center bg-white border border-[#00ac9c] rounded-full shadow-md transition-shadow"
            style={{ padding: '0.1rem' }}
          >
            <FaCircleInfo color="#00ac9c" size={16} />
          </button>
        )}
      </CircularProgressbarWithChildren>
    </div>
  </div>
)

// AcresSection Component
const AcresSection = ({ title, value, isDemoUser }) => (
  <div className="text-center w-[6rem]">
    <h3 className="text-sm font-semibold">{title}</h3>
    <p className="font-semibold text-[#457b9d]">
      <span className={`${isDemoUser ? 'blur' : ''}`}>{value}</span>
    </p>
  </div>
)

const MobileRanchCardView = ({
  ranchParcels,
  ranch,
  loading,
  summary,
  reportPDF,
  downloadReport,
  onRunReportClick,
  hydraScoreColor,
  scoreHoverText,
  setPopupContent,
  isDemoUser,
}) => {
  const runReportRef = useRef(null)
  const { checkAbility } = useAbility()

  const enableReportGeneration = checkAbility('report_generation')

  return (
    <div className="bg-white shadow-md rounded-lg p-4 mb-4 flex flex-col">
      {loading ? (
        <div className="flex justify-center items-center h-32">
          <img src={spinner} alt="Loading..." className="h-12" />
        </div>
      ) : (
        <>
          {/* Card Header */}
          <div className="relative flex justify-between items-center mb-2">
            <div className="relative">
              <div className="relative flex justify-between items-center mb-2">
                <h3 className="text-lg font-semibold text-center">
                  {ranch.ranch_name}
                </h3>
                <button
                  aria-label="Share"
                  onClick={(e) => {
                    const locations = ranchParcels[ranch.ranch_id].map(
                      (parcel) => {
                        return {
                          lat: parcel.data.geolocation.lat,
                          lon: parcel.data.geolocation.lng,
                        }
                      }
                    )
                    navigator
                      .share({
                        title: 'Hydra score parcel(s)',
                        text: 'Click link to see shared parcel(s)',
                        url: `https://app.hydrascore.com/?locations=${btoa(JSON.stringify(locations))}`,
                      })
                      .catch((error) => {
                        console.error('Sharing failed', error)
                      })
                  }}
                  title="Share this parcel"
                  className="mr-4" // Add some margin if necessary
                >
                  <FaShare />
                </button>
              </div>
            </div>
            {ranch.mod_time && (
              <Moment
                className="absolute top-2 right-2 bg-[#3377cc] text-white text-xs font-semibold rounded-md px-2 py-1 shadow-lg"
                fromNow
              >
                {ranch.mod_time}
              </Moment>
            )}
          </div>

          {/* Score Sections */}
          <div className="flex flex-col items-center mb-2 space-y-2">
            {/* First Row: Hydra Score and Soil Score */}
            <div className="flex justify-around items-center w-full">
              <ScoreSection
                title="Hydra Score"
                value={summary['Geometrically Weighted Aggregate HydraSCORE']}
                hoverText={summary['hover']}
                color={hydraScoreColor(
                  summary['Geometrically Weighted Aggregate HydraSCORE']
                )}
                onInfoClick={() => setPopupContent(summary['hover'])}
                needUpgrade={!checkAbility('hydra_score')}
              />
              <ScoreSection
                title="Soil Score"
                value={summary['Geometrically Weighted Aggregate SS']}
                hoverText={scoreHoverText(
                  'ss',
                  summary['Geometrically Weighted Aggregate SS']
                )}
                color={hydraScoreColor(
                  summary['Geometrically Weighted Aggregate SS']
                )}
                onInfoClick={() =>
                  setPopupContent(
                    scoreHoverText(
                      'ss',
                      summary['Geometrically Weighted Aggregate SS']
                    )
                  )
                }
                needUpgrade={!checkAbility('soil_score')}
              />
            </div>

            {/* Second Row: Total Acres and Low Score Acres */}
            <div className="flex justify-around items-center w-full">
              <AcresSection
                title="Total Acres"
                value={summary['Total Acres']}
                isDemoUser={isDemoUser}
              />
              <AcresSection
                title="Low Hydra Score Acres"
                value={
                  summary['Acres Poor Rating'] === 'NR'
                    ? 'Not Yet Rated'
                    : `${summary['Acres Poor Rating'].toFixed(2)} ac (${(
                        (summary['Acres Poor Rating'] /
                          summary['Total Acres']) *
                        100
                      ).toFixed(2)}%)`
                }
                isDemoUser={isDemoUser}
              />
              <AcresSection
                title="Low Soil Score Acres"
                value={
                  summary['Acres Poor Rating SS'] === 'NR'
                    ? 'Not Yet Rated'
                    : `${summary['Acres Poor Rating SS'].toFixed(2)} ac (${(
                        (summary['Acres Poor Rating SS'] /
                          summary['Total Acres']) *
                        100
                      ).toFixed(2)}%)`
                }
                isDemoUser={isDemoUser}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col items-center mt-4 space-y-2">
            <div className="relative">
              <UpgradeLockIconLayout
                position={'w-fit -left-1 top-0'}
                className="min-h-4 bg-transparent"
                iconSize={{ width: 24, height: 24 }}
                show={!enableReportGeneration}
              />
              <button
                ref={runReportRef}
                onClick={onRunReportClick}
                disabled={!enableReportGeneration}
                className="disabled:cursor-not-allowed disabled:opacity-50
                btn btn-custom"
              >
                Run Report
              </button>
            </div>

            {Object.keys(reportPDF).length > 0 && (
              <button
                className="text-[#457b9d] font-medium flex items-center space-x-1"
                onClick={downloadReport}
              >
                <IoMdDownload className="inline" />
                <span>{reportPDF.download}</span>
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default MobileRanchCardView
